import React, { useState, useEffect, useRef } from 'react';
import { SlidersHorizontal, X, Eraser } from 'lucide-react';
import { Button } from './ui/button';
import { useT } from '../services/i18n';

const FilterPanel = ({ children, resultCount, activeCount = 0, label, onReset }) => {
  const { t } = useT();
  label = label || t('cat.filters');
  const [open, setOpen] = useState(false);
  const [drag, setDrag] = useState(0);
  const start = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open]);
  useEffect(() => { if (!open) setDrag(0); }, [open]);

  const onHandleStart = (e) => { start.current = e.touches[0].clientY; };
  const onHandleMove = (e) => { if (start.current != null) setDrag(Math.max(0, e.touches[0].clientY - start.current)); };
  const onHandleEnd = () => { if (drag > 90) setOpen(false); else setDrag(0); start.current = null; };

  return (
    <>
      <div className="lg:hidden mb-4">
        <button
          type="button"
          data-testid="mobile-filters-button"
          onClick={() => setOpen(true)}
          className="w-full flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm text-sm font-medium text-gray-800 hover:border-orange-400"
        >
          <span className="flex items-center gap-2">
            <SlidersHorizontal size={18} className="text-orange-500" /> {label}
            {activeCount > 0 && (
              <span data-testid="mobile-filters-count" className="bg-orange-500 text-white text-xs rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">{activeCount}</span>
            )}
          </span>
          <span className="text-xs text-gray-500" data-testid="mobile-filters-result-count">{t('cat.found', { n: resultCount, count: resultCount })}</span>
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filters-drawer">
          <div className="absolute inset-0 bg-black/50 animate-in fade-in duration-200" onClick={() => setOpen(false)} data-testid="mobile-filters-backdrop" />
          <div
            className="absolute inset-x-0 bottom-0 max-h-[85vh] bg-gray-50 shadow-2xl flex flex-col rounded-t-2xl animate-in slide-in-from-bottom duration-300"
            style={{ transform: drag ? `translateY(${drag}px)` : undefined, transition: drag ? 'none' : undefined, paddingBottom: 'env(safe-area-inset-bottom)' }}
            data-testid="mobile-filters-sheet"
          >
            <div className="pt-2 pb-1 flex justify-center cursor-grab touch-none" onTouchStart={onHandleStart} onTouchMove={onHandleMove} onTouchEnd={onHandleEnd} data-testid="mobile-filters-handle">
              <span className="h-1.5 w-12 rounded-full bg-gray-300" />
            </div>
            <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-200" onTouchStart={onHandleStart} onTouchMove={onHandleMove} onTouchEnd={onHandleEnd}>
              <span className="font-semibold text-gray-900 flex items-center gap-2"><SlidersHorizontal size={18} className="text-orange-500" /> {label}
                {activeCount > 0 && <span className="bg-orange-500 text-white text-xs rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center" data-testid="mobile-filters-sheet-active">{activeCount}</span>}
              </span>
              <span className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-full px-2.5 py-1 tabular-nums" data-testid="mobile-filters-sheet-count" aria-live="polite">{t('cat.found', { n: resultCount, count: resultCount })}</span>
                <button data-testid="mobile-filters-close" onClick={() => setOpen(false)} aria-label="Fermer" className="p-1 text-gray-500 hover:text-gray-900"><X size={22} /></button>
              </span>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain p-3">{children}</div>
            <div className="p-3 border-t bg-white flex items-center gap-2">
              {onReset && activeCount > 0 && (
                <button type="button" data-testid="mobile-filters-reset" onClick={onReset} className="h-12 shrink-0 inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 active:scale-95 transition-transform">
                  <Eraser size={16} /> Tout effacer
                </button>
              )}
              <Button data-testid="mobile-filters-show" onClick={() => setOpen(false)} className="flex-1 h-12 text-base bg-orange-500 hover:bg-orange-600">
                {t('cat.showResults', { n: resultCount })}
              </Button>
            </div>
          </div>
        </div>
      )}

      <aside className="hidden lg:block lg:col-span-1">{children}</aside>
    </>
  );
};

export default FilterPanel;
